<template>
  <div v-if="display" id="video-overlay-container">
    <div id="video-player-overlay">
      <div>
        <div id="video-player-overlay-content">
          <div class="text-h5 mb-7">You must watch the entire video in order to progress.</div>
          <vue-plyr ref="plyr" :options="options" :key="embedSrc">
            <div class="plyr__video-embed">
              <iframe
                  :src="embedSrc"
                  allowfullscreen
                  allowtransparency
                  allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                  referrerpolicy="origin-when-cross-origin"
              ></iframe>
            </div>
          </vue-plyr>
        </div>
      </div>
    </div>
    <div id="video-overlay" />
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  props: {
    currentSection: Number,
    videoLink: String
  },
  data() {
    return {
      options: {
        settings: ['captions', 'quality'],
        clickToPlay: true,
        youtube: {
          playsinline: 1,
          rel: 0,
          modestbranding: 1,
          iv_load_policy: 3
        },
        listeners: {
          seek: (e) => {
            var currentTime = this.player.currentTime;
            var newTime = this.getTargetTime(this.player, e);
            // We only want rewind functionality
            // Therefore, disallow moving forward
            if (newTime > currentTime) {
              /*
                When this listener returns false, the default behavior
                is removed. As such, if you want to enable seeking forward
                for the mandatory video, then comment out the line below
              */
              return false;
            }
          }
        }
      },
      display: true,
      currentTime: 0,
      player: null,
      initAttempts: 0,
      endedHandler: null
    }
  },
  computed: {
    ...mapState(['videosWatched']),

    // Display the video if the video link is valid and not already watched
    shouldDisplayVideo() {
      return !(this.videoLink && this.videosWatched[this.currentSection]);
    },

    embedSrc() {
      if (!this.videoLink) return '';

      let url;
      try {
        url = new URL(this.videoLink, window.location.origin);
      } catch (e) {
        return this.videoLink;
      }

      const host = (url.hostname || '').toLowerCase();
      let videoId = null;

      if (host.includes('youtu.be')) {
        videoId = url.pathname.replace('/', '') || null;
      } else if (host.includes('youtube.com')) {
        const path = url.pathname || '';
        if (path === '/watch') {
          videoId = url.searchParams.get('v');
        } else if (path.startsWith('/embed/')) {
          videoId = path.split('/embed/')[1]?.split('/')[0] || null;
        } else if (path.startsWith('/shorts/')) {
          videoId = path.split('/shorts/')[1]?.split('/')[0] || null;
        }
      }

      if (!videoId) {
        const original = new URL(url.toString());
        original.searchParams.set('enablejsapi', '1');
        original.searchParams.set('playsinline', '1');
        original.searchParams.set('origin', window.location.origin);
        return original.toString();
      }

      const embed = new URL(`https://www.youtube.com/embed/${videoId}`);

      const start = url.searchParams.get('start') || url.searchParams.get('t');
      if (start) embed.searchParams.set('start', start);

      const end = url.searchParams.get('end');
      if (end) embed.searchParams.set('end', end);

      embed.searchParams.set('enablejsapi', '1');
      embed.searchParams.set('playsinline', '1');
      embed.searchParams.set('origin', window.location.origin);
      embed.searchParams.set('rel', '0');
      embed.searchParams.set('modestbranding', '1');
      embed.searchParams.set('iv_load_policy', '3');

      return embed.toString();
    }
  },
  methods: {
    // Get the current time of the video
    getTargetTime(plyr, input) {
      if (
          typeof input === "object" &&
          (input.type === "input" || input.type === "change")
      ) {
        return input.target.value / input.target.max * plyr.media.duration;
      } else {
        // We're assuming its a number
        return Number(input);
      }
    },

    initPlayer() {
      const plyrComp = this.$refs.plyr;
      const player = plyrComp && plyrComp.player ? plyrComp.player : null;

      if (!player) {
        this.initAttempts += 1;
        if (this.initAttempts < 30) {
          requestAnimationFrame(this.initPlayer);
        }
        return;
      }

      this.player = player;

      if (this.endedHandler) {
        this.player.off('ended', this.endedHandler);
      }

      this.endedHandler = () => {
        this.$store.commit('ADD_VIDEOS_WATCHED', this.currentSection);
        this.display = !this.display;
      };

      this.player.on('ended', this.endedHandler);
    }
  },
  mounted () {
    this.display = this.shouldDisplayVideo;

    if (this.display) {
      this.$nextTick(() => {
        this.initPlayer();
      });
    }
  },
  beforeDestroy() {
    if (this.player && this.endedHandler) {
      this.player.off('ended', this.endedHandler);
    }
  }
}
</script>

<style>
#video-overlay-container {
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: row;
  justify-content: center;
  color: #fff;
  z-index: 1000;
}

#video-player-overlay-content {
  margin-top: 0;
}

#video-player-overlay {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 100;
  width: 60%;
  max-width: 95vw;
  max-height: 90vh;
  overflow-y: auto;
  padding-bottom: 20px;
  box-sizing: border-box;
}

#video-overlay {
  background: rgba(0, 0, 0, .9);
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 5;
  top: 0;
  left: 0;
  right: 0;
  pointer-events: none;
}

@media (max-width: 768px) {
  #video-player-overlay {
    width: 95%;
    max-height: 92vh;
  }
}
</style>
